import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, API, getToken } from "@/lib/api";
import { formatMoney, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { CategoryField } from "@/components/entity/CategoryField";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Users2, Loader2, Sparkles, CheckCircle2, ChevronDown, ChevronRight, FileSpreadsheet, Download, Trash2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const CURRENCIES = ["INR", "USD", "EUR", "GBP"];
const monthEnd = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
};

export default function Payroll() {
  const { user } = useAuth();
  const canDelete = ["super admin", "admin", "supervision"].includes(String(user?.role || "").toLowerCase());
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [stage, setStage] = useState("pick"); // pick | parsing | review | posting
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [meta, setMeta] = useState({ month: "", date: monthEnd(), currency: "INR", category: "Payroll / Salaries", create_statutory_payables: true });
  const [attachment, setAttachment] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [tab, setTab] = useState("salary");
  const isStipend = tab === "stipend";
  const memberLabel = isStipend ? "interns" : "employees";

  const loadRuns = useCallback(async () => {
    try {
      const { data } = await api.get("/payroll/runs");
      setRuns(data.runs || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    (async () => { setLoading(true); await loadRuns(); setLoading(false); })();
  }, [loadRuns]);

  const reset = () => {
    setStage("pick"); setFile(null); setParsed(null); setAttachment(null);
    setMeta({ month: "", date: monthEnd(), currency: "INR",
      category: isStipend ? "Stipends" : "Payroll / Salaries",
      create_statutory_payables: !isStipend });
  };

  const startUpload = () => { reset(); setOpen(true); };
  const switchTab = (t) => { setTab(t); setExpanded(null); };
  const shownRuns = runs.filter((r) => (r.payroll_type || "salary") === tab);

  const deleteRun = async (run, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete this ${isStipend ? "stipend" : "payroll"} run (${formatMoney(run.amount, run.currency)})? Any statutory payables it created will also be removed.`)) return;
    try {
      const { data } = await api.delete(`/payroll/runs/${run.id}`);
      toast.success(`Run deleted${data.deleted_statutory ? ` · ${data.deleted_statutory} statutory payable(s) removed` : ""}`);
      if (expanded === run.id) setExpanded(null);
      loadRuns();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete run");
    }
  };

  const parse = async () => {
    if (!file) return toast.error("Choose a payroll Excel file");
    setStage("parsing");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // Parse first — a rejected sheet throws here and never uploads an orphan file.
      const { data } = await api.post(`/payroll/parse?kind=${tab}`, fd);
      setParsed(data);
      const af = new FormData();
      af.append("file", file);
      const { data: att } = await api.post("/attachments", af);
      setAttachment(att);
      const guess = (file.name.match(/([A-Za-z]+\s*\d{4})/) || [])[1] || "";
      setMeta((m) => ({ ...m, month: guess }));
      setStage("review");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read the payroll sheet");
      setStage("pick");
    }
  };

  const setEmp = (i, k, v) =>
    setParsed((p) => {
      const employees = p.employees.map((e, idx) => (idx === i ? { ...e, [k]: Number(v) } : e));
      return { ...p, employees };
    });

  const totalCtc = parsed ? parsed.employees.reduce((s, e) => s + (Number(e.ctc) || 0), 0) : 0;
  const totalNet = parsed ? parsed.employees.reduce((s, e) => s + (Number(e.net) || 0), 0) : 0;
  // Stipends book the consolidated Net Pay as the expense; salaries book total CTC.
  const expenseTotal = isStipend ? totalNet : totalCtc;

  const post = async (force = false) => {
    setStage("posting");
    try {
      const { data } = await api.post("/payroll/post", {
        ...meta,
        force,
        payroll_type: tab,
        employees: parsed.employees,
        attachments: attachment ? [attachment] : [],
      });
      let msg = `${isStipend ? "Stipends" : "Payroll"} booked: ${formatMoney(data.amount, meta.currency)} (${data.member_count ?? data.employee_count} ${memberLabel})`;
      if (data.statutory_payables?.length) msg += ` · ${data.statutory_payables.length} statutory payable(s)`;
      toast.success(msg);
      setOpen(false);
      reset();
      loadRuns();
    } catch (e) {
      if (e.response?.status === 409) {
        setStage("review");
        if (window.confirm(e.response?.data?.detail || "A payroll for this period already exists. Book it again?")) {
          return post(true);
        }
        return;
      }
      toast.error(e.response?.data?.detail || "Could not post payroll");
      setStage("review");
    }
  };

  return (
    <div className="space-y-6" data-testid="payroll-page">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Users2 className="h-6 w-6 text-brand" /> Payroll
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {isStipend
              ? "Upload your interns' stipend sheet. It's booked as one Stipends expense at the total amount, with the full per-intern breakdown stored on it."
              : "Upload your monthly payroll sheet. It's booked as one expense at the total Cost to Company (Ind-AS employee-benefit expense), with the full per-employee breakdown stored on it."}
          </p>
        </div>
        <Button onClick={startUpload} className="gap-2 shrink-0" data-testid="upload-payroll-btn">
          <FileSpreadsheet className="h-4 w-4" /> {isStipend ? "Upload Stipend Sheet" : "Upload Payroll Sheet"}
        </Button>
      </div>

      <div className="inline-flex rounded-lg border border-border bg-muted/40 p-1" role="tablist" data-testid="payroll-tabs">
        {[["salary", "Employees"], ["stipend", "Interns (Stipend)"]].map(([k, label]) => (
          <button
            key={k}
            role="tab"
            aria-selected={tab === k}
            onClick={() => switchTab(k)}
            className={`px-4 py-1.5 text-sm rounded-md transition-colors ${
              tab === k ? "bg-brand text-white" : "text-muted-foreground hover:text-foreground"
            }`}
            data-testid={`payroll-tab-${k}`}
          >
            {label}
          </button>
        ))}
      </div>

      <Card className="overflow-hidden">
        <div className="px-5 py-3 border-b border-border">
          <h3 className="font-heading font-semibold">{isStipend ? "Stipend runs" : "Payroll runs"}</h3>
        </div>
        {loading ? (
          <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-brand" /></div>
        ) : shownRuns.length === 0 ? (
          <div className="p-10 text-center text-sm text-muted-foreground">No {isStipend ? "stipends" : "payroll"} uploaded yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {shownRuns.map((r) => {
              const pr = r.payroll || {};
              const t = pr.totals || {};
              const isOpen = expanded === r.id;
              return (
                <div key={r.id} data-testid="payroll-run">
                  <div className="w-full flex items-center hover:bg-muted/50">
                    <button
                      onClick={() => setExpanded(isOpen ? null : r.id)}
                      className="flex-1 min-w-0 px-5 py-3 flex items-center justify-between gap-3 text-left"
                      data-testid="payroll-run-toggle"
                    >
                      <div className="flex items-center gap-2 min-w-0">
                        {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">{r.description}</p>
                          <div className="text-xs text-muted-foreground flex items-center gap-1 flex-wrap">
                            {formatDate(r.date)} · {pr.employee_count || 0} {memberLabel} · <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">{r.status}</Badge>
                          </div>
                        </div>
                      </div>
                      <span className="font-mono text-sm shrink-0">{formatMoney(r.amount, r.currency)}</span>
                    </button>
                    {canDelete && (
                      <button
                        onClick={(e) => deleteRun(r, e)}
                        className="px-3 py-3 text-muted-foreground hover:text-danger shrink-0"
                        title="Delete this run"
                        data-testid="payroll-run-delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {isOpen && (
                    <div className="px-5 pb-4">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3 text-xs">
                        {[["Total CTC", t.ctc], ["Gross", t.gross], ["Net Pay", t.net], ["Deductions", t.deductions]].map(([l, v]) => (
                          <div key={l} className="rounded border border-border p-2">
                            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</p>
                            <p className="font-mono font-medium">{formatMoney(v || 0, r.currency)}</p>
                          </div>
                        ))}
                      </div>
                      {r.attachments?.[0] && (
                        <a href={`${API}/attachments/${r.attachments[0].id}/download?auth=${getToken()}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-brand hover:underline mb-2">
                          <Download className="h-3 w-3" /> {r.attachments[0].filename}
                        </a>
                      )}
                      <div className="max-h-64 overflow-y-auto rounded border border-border">
                        <table className="w-full text-xs">
                          <thead className="bg-muted/60 sticky top-0">
                            {isStipend ? (
                              <tr className="text-left">
                                <th className="px-2 py-1.5">Employee Code</th><th className="px-2 py-1.5">Employee Name</th>
                                <th className="px-2 py-1.5 text-right">Present Days</th><th className="px-2 py-1.5 text-right">Gross CTC</th><th className="px-2 py-1.5 text-right">Net Pay</th>
                              </tr>
                            ) : (
                              <tr className="text-left">
                                <th className="px-2 py-1.5">Employee</th><th className="px-2 py-1.5">Dept</th>
                                <th className="px-2 py-1.5 text-right">Gross</th><th className="px-2 py-1.5 text-right">CTC</th><th className="px-2 py-1.5 text-right">Net</th>
                              </tr>
                            )}
                          </thead>
                          <tbody>
                            {(pr.employees || []).map((e, i) => (
                              <tr key={i} className="border-t border-border/60">
                                {isStipend ? (
                                  <>
                                    <td className="px-2 py-1 text-muted-foreground">{e.emp_no || "—"}</td>
                                    <td className="px-2 py-1 truncate max-w-[180px]">{e.name}</td>
                                    <td className="px-2 py-1 text-right font-mono">{Number(e.present_days) || 0}</td>
                                    <td className="px-2 py-1 text-right font-mono">{formatMoney(e.ctc, r.currency)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{formatMoney(e.net, r.currency)}</td>
                                  </>
                                ) : (
                                  <>
                                    <td className="px-2 py-1 truncate max-w-[180px]">{e.name}</td>
                                    <td className="px-2 py-1 text-muted-foreground">{e.department || "—"}</td>
                                    <td className="px-2 py-1 text-right font-mono">{formatMoney(e.gross, r.currency)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{formatMoney(e.ctc, r.currency)}</td>
                                    <td className="px-2 py-1 text-right font-mono">{formatMoney(e.net, r.currency)}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Upload + review dialog */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{isStipend ? "Upload Stipend Sheet" : "Upload Payroll Sheet"}</DialogTitle>
            <DialogDescription>
              {stage === "pick" && (isStipend
                ? "Upload the stipend sheet in the standard format: Employee Code, Employee Name, Present Days, Gross CTC, Net Pay. Net Pay is booked as the stipend expense."
                : "Upload the monthly payroll Excel — any layout, we auto-detect the columns.")}
              {stage === "parsing" && "Reading the payroll sheet…"}
              {stage === "review" && `Review the run, then book it as a ${isStipend ? "Stipends" : "payroll"} expense.`}
              {stage === "posting" && "Booking payroll…"}
            </DialogDescription>
          </DialogHeader>

          {stage === "pick" && (
            <div className="py-2 space-y-3">
              {isStipend && (
                <div className="rounded-md border border-brand/25 bg-brand/5 px-3 py-2.5 text-xs" data-testid="stipend-template-hint">
                  <p className="font-medium text-foreground mb-1">Standard stipend format required</p>
                  <p className="text-muted-foreground">
                    Columns (exactly): <span className="text-foreground">Employee Code · Employee Name · Present Days · Gross CTC · Net Pay</span>. Net Pay is booked as the stipend expense.
                  </p>
                  <a
                    href={`${API}/payroll/stipend-template`}
                    className="inline-flex items-center gap-1 text-brand hover:underline mt-1.5"
                    data-testid="stipend-template-download"
                  >
                    <Download className="h-3 w-3" /> Download template
                  </a>
                </div>
              )}
              <div>
                <Label className="text-xs mb-1.5 block">{isStipend ? "Stipend" : "Payroll"} file (.xlsx)</Label>
                <Input type="file" accept=".xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="payroll-file-input" />
              </div>
            </div>
          )}

          {(stage === "parsing" || stage === "posting") && (
            <div className="py-10 flex flex-col items-center gap-3">
              <Loader2 className="h-8 w-8 animate-spin text-brand" />
              <p className="text-sm text-muted-foreground">{stage === "parsing" ? "Detecting columns & reading employees…" : "Booking payroll expense…"}</p>
            </div>
          )}

          {stage === "review" && parsed && (
            <div className="space-y-4" data-testid="payroll-review">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Pay period</Label>
                  <Input value={meta.month} onChange={(e) => setMeta({ ...meta, month: e.target.value })} placeholder="e.g. September 2026" className="mt-1 h-9" data-testid="payroll-month" />
                </div>
                <div>
                  <Label className="text-xs">Expense date</Label>
                  <Input type="date" value={meta.date} onChange={(e) => setMeta({ ...meta, date: e.target.value })} className="mt-1 h-9" data-testid="payroll-date" />
                </div>
                <div>
                  <Label className="text-xs">Category</Label>
                  <div className="mt-1"><CategoryField catType="expense" value={meta.category} onChange={(v) => setMeta({ ...meta, category: v })} testid="payroll-category" /></div>
                </div>
                <div>
                  <Label className="text-xs">Currency</Label>
                  <select value={meta.currency} onChange={(e) => setMeta({ ...meta, currency: e.target.value })} className="mt-1 w-full h-9 rounded-md bg-background border border-input px-2 text-sm" data-testid="payroll-currency">
                    {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="rounded-lg border border-brand/30 bg-brand/5 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{isStipend ? "Total Net Pay (expense)" : "Total CTC (expense)"}</p>
                  <p className="text-lg font-mono font-semibold text-brand" data-testid="payroll-total-ctc">{formatMoney(expenseTotal, meta.currency)}</p>
                </div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">{isStipend ? "Gross CTC" : "Gross"}</p><p className="font-mono">{formatMoney(isStipend ? totalCtc : parsed.totals.gross, meta.currency)}</p></div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">Net Pay</p><p className="font-mono">{formatMoney(parsed.totals.net, meta.currency)}</p></div>
                <div className="rounded-lg border border-border p-3"><p className="text-[10px] uppercase text-muted-foreground">{isStipend ? "Interns" : "Employees"}</p><p className="font-mono">{parsed.employee_count}</p></div>
              </div>

              <div className="rounded-lg border border-border p-3 space-y-2" data-testid="payroll-statutory" hidden={isStipend} style={isStipend ? { display: "none" } : undefined}>
                <label className="flex items-center gap-2 text-sm font-medium cursor-pointer">
                  <input
                    type="checkbox"
                    checked={meta.create_statutory_payables}
                    onChange={(e) => setMeta({ ...meta, create_statutory_payables: e.target.checked })}
                    data-testid="payroll-statutory-toggle"
                  />
                  Book statutory dues as separate payables to authorities (Ind-AS)
                </label>
                {meta.create_statutory_payables && (
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded border border-border p-2" data-testid="payroll-stat-pf">
                      <p className="text-[10px] uppercase text-muted-foreground">PF → EPFO</p>
                      <p className="font-mono">{formatMoney((parsed.totals.pf || 0) + (parsed.totals.employer_pf || 0), meta.currency)}</p>
                    </div>
                    <div className="rounded border border-border p-2" data-testid="payroll-stat-tds">
                      <p className="text-[10px] uppercase text-muted-foreground">TDS → Income Tax</p>
                      <p className="font-mono">{formatMoney(parsed.totals.income_tax || 0, meta.currency)}</p>
                    </div>
                    <div className="rounded border border-border p-2" data-testid="payroll-stat-pt">
                      <p className="text-[10px] uppercase text-muted-foreground">PT → State</p>
                      <p className="font-mono">{formatMoney(parsed.totals.professional_tax || 0, meta.currency)}</p>
                    </div>
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Creates payables to EPFO, the Income Tax Dept and the State PT authority (zero-value ones are skipped) for tracking &amp; remittance. The total CTC above stays the single P&amp;L expense.
                </p>
              </div>

              <div className="max-h-72 overflow-y-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/60 sticky top-0">
                    {isStipend ? (
                      <tr className="text-left">
                        <th className="px-2 py-1.5">Employee Code</th><th className="px-2 py-1.5">Employee Name</th>
                        <th className="px-2 py-1.5 text-right">Present Days</th><th className="px-2 py-1.5 text-right">Gross CTC</th><th className="px-2 py-1.5 text-right">Net Pay</th>
                      </tr>
                    ) : (
                      <tr className="text-left">
                        <th className="px-2 py-1.5">Employee</th><th className="px-2 py-1.5">Dept</th>
                        <th className="px-2 py-1.5 text-right">Gross</th><th className="px-2 py-1.5 text-right">CTC</th><th className="px-2 py-1.5 text-right">Net</th>
                      </tr>
                    )}
                  </thead>
                  <tbody>
                    {parsed.employees.map((e, i) => (
                      <tr key={i} className="border-t border-border/60" data-testid="payroll-emp-row">
                        {isStipend ? (
                          <>
                            <td className="px-2 py-1 text-muted-foreground">{e.emp_no || "—"}</td>
                            <td className="px-2 py-1 truncate max-w-[180px]">{e.name}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(e.present_days) || 0}</td>
                            <td className="px-2 py-1 text-right font-mono">{formatMoney(e.ctc, meta.currency)}</td>
                            <td className="px-1 py-1 text-right">
                              <Input value={e.net} onChange={(ev) => setEmp(i, "net", ev.target.value)} className="h-7 text-xs text-right w-24 ml-auto" type="number" data-testid={`stipend-net-${i}`} />
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-2 py-1 truncate max-w-[180px]">{e.name}</td>
                            <td className="px-2 py-1 text-muted-foreground">{e.department || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{formatMoney(e.gross, meta.currency)}</td>
                            <td className="px-1 py-1 text-right">
                              <Input value={e.ctc} onChange={(ev) => setEmp(i, "ctc", ev.target.value)} className="h-7 text-xs text-right w-24 ml-auto" type="number" />
                            </td>
                            <td className="px-2 py-1 text-right font-mono">{formatMoney(e.net, meta.currency)}</td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsed.skipped > 0 && <p className="text-[11px] text-muted-foreground">{parsed.skipped} row(s) without a name/amount were skipped.</p>}
            </div>
          )}

          <DialogFooter>
            {stage === "pick" && (
              <>
                <Button variant="outline" onClick={() => { setOpen(false); reset(); }}>Cancel</Button>
                <Button onClick={parse} disabled={!file} className="gap-2" data-testid="payroll-parse-btn"><Sparkles className="h-4 w-4" /> Read sheet</Button>
              </>
            )}
            {stage === "review" && (
              <>
                <Button variant="outline" onClick={reset}>Start over</Button>
                <Button onClick={() => post()} disabled={expenseTotal <= 0} className="gap-2" data-testid="payroll-post-btn"><CheckCircle2 className="h-4 w-4" /> Book {formatMoney(expenseTotal, meta.currency)}</Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
